import { useEffect, useState } from 'react';
import { api } from './api.js';
import { stamp as when, since as hours } from './format.js';

/**
 * The handover panel.
 *
 * Two halves on purpose. The top is the brief — what the person coming on actually needs
 * before they accept the seat. The bottom is the act of taking it. Putting the outstanding
 * P1s directly above the button is the point: nobody should take a position without having
 * been shown what is still open on it.
 */
export function Handover({ incidentId, position, canHandOver, session, onDone, onClose }) {
  const [brief, setBrief] = useState(null);
  const [name, setName] = useState('');
  // Whether the person at the keyboard is taking the seat themselves. Defaults to yes,
  // because that is what happens nearly every time — and it is the ONLY way the seat gets
  // linked to an account rather than to a typed name.
  const [self, setSelf] = useState(true);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setBrief(null);
    api.handoverBrief(incidentId, position.id).then(setBrief).catch(() => setError('Could not load the handover record.'));
    if (self) setName(session?.user?.displayName ?? '');
  }, [incidentId, position.id]);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setError(null);
    try {
      // userId, not just a name. Without it the assignment records WHO BY NAME and the
      // server has no way to know the signed-in person is sitting in the seat — which is
      // what silently blocked a section lead from writing their own function's objective.
      await api.handOver(incidentId, position.id, name.trim(), notes.trim(),
        self ? session?.user?.id ?? null : null);
      setName(''); setNotes('');
      setBrief(await api.handoverBrief(incidentId, position.id));
      onDone?.();
    } catch (err) {
      setError(err.status === 403 ? 'Your role cannot hand this position over.' : 'That handover did not save.');
    } finally { setBusy(false); }
  }

  return (
    <div className="hoWrap" onClick={onClose}>
      <div className="ho" onClick={(e) => e.stopPropagation()}>
        <div className="hoHead">
          <div>
            <h3>{position.name}</h3>
            <p>{brief?.current
              ? `${brief.current.holder_name} — on watch ${hours(brief.current.from_at)}`
              : 'Nobody currently holds this position'}</p>
          </div>
          <button className="hoX" onClick={onClose} aria-label="Close">×</button>
        </div>

        {!brief && !error && <div className="empty">Loading…</div>}

        {brief && (
          <div className="hoBody">
            <section>
              <h4>Outstanding on this position</h4>
              {brief.openItems.length === 0
                ? <p className="hoNone">Nothing outstanding.</p>
                : (
                  <>
                    {brief.openP1 > 0 && (
                      <p className="hoWarn">{brief.openP1} Priority 1 action{brief.openP1 === 1 ? '' : 's'} still open.</p>
                    )}
                    <ul className="hoList">
                      {brief.openItems.map((i) => (
                        <li key={i.id}><span className={`pri p${i.priority}`}>P{i.priority}</span> {i.text}</li>
                      ))}
                    </ul>
                  </>
                )}
            </section>

            <section>
              <h4>Completed this watch</h4>
              {brief.completedThisWatch.length === 0
                ? <p className="hoNone">Nothing recorded on this watch yet.</p>
                : (
                  <ul className="hoList">
                    {brief.completedThisWatch.map((e, n) => (
                      <li key={n}><span className="hoTime">{when(e.at)}</span> {e.message}</li>
                    ))}
                  </ul>
                )}
            </section>

            <section>
              <h4>Watch history</h4>
              {brief.watches.length === 0
                ? <p className="hoNone">No watches recorded.</p>
                : (
                  <table className="hoTable">
                    <tbody>
                      {brief.watches.map((w) => (
                        <tr key={w.id} className={w.to_at ? '' : 'now'}>
                          <td><b>{w.holder_name}</b></td>
                          <td>{when(w.from_at)} → {w.to_at ? when(w.to_at) : 'on watch'}</td>
                          <td>{hours(w.from_at, w.to_at)}</td>
                          <td className="hoNotes">{w.handover_notes || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </section>

            {canHandOver && (
              <form className="hoForm" onSubmit={submit}>
                <h4>{brief.current ? 'Hand over to' : 'Assume this position'}</h4>

                {/* Who is taking it. This is not cosmetic: choosing "me" links the seat to
                    the signed-in ACCOUNT, which is what lets the system know whose function
                    this is. A typed name alone records the watch correctly but leaves the
                    seat held by nobody the software can recognise. */}
                <div className="hoWho">
                  <label>
                    <input type="radio" checked={self}
                           onChange={() => { setSelf(true); setName(session?.user?.displayName ?? ''); }} />
                    <span>I am taking it{session?.user?.displayName ? ` — ${session.user.displayName}` : ''}</span>
                  </label>
                  <label>
                    <input type="radio" checked={!self}
                           onChange={() => { setSelf(false); setName(''); }} />
                    <span>Somebody else</span>
                  </label>
                </div>

                <input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="Name of the person taking over" maxLength={120} required
                       readOnly={self} />
                {!self && (
                  <small className="hoNote">
                    Recorded against this name. They will not be recognised as holding the
                    position until they take it under their own sign-in.
                  </small>
                )}
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                          placeholder="Anything the incoming person must know — this is recorded against the outgoing watch"
                          rows={3} maxLength={2000} />
                <button className="btn" type="submit" disabled={busy || !name.trim()}>
                  {busy ? 'Recording…' : brief.current ? 'Record handover' : 'Assume position'}
                </button>
              </form>
            )}
          </div>
        )}

        {error && <p className="err">{error}</p>}
      </div>
    </div>
  );
}
